import {
  Button,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Select,
  SimpleGrid,
  Stack,
  Text,
  useToast,
} from "@chakra-ui/react";
import BoxShadowContainer from "components/UIComponents/BoxShadowContainer";
import { RequestMethod, RequestName } from "hooks/api/apiHookTypes";
import useApiRequest from "hooks/api/useApiHook";
import { FC, useEffect, useState } from "react";
import {
  PAYOUT_MIN_USD,
  computePayoutPreview,
  formatUsd,
  serverErrorMessage,
} from "./helpers";

type Props = {
  apiToken: string;
  // Available referral balance (ready_to_withdraw) — the gross that would be
  // paid out. The server recomputes it; this is for the pre-submit preview and
  // the client-side below-minimum guard only.
  available: number;
  onRequested: () => void;
};

// External crypto payout request. Currency list comes from the API (never
// hardcoded, US3); the client picks currency + address and previews the
// server-defined fee, but every amount displayed after submit is the server's.
const PayoutForm: FC<Props> = ({ apiToken, available, onRequested }) => {
  const toast = useToast();
  const [currency, setCurrency] = useState("");
  const [address, setAddress] = useState("");
  const [trigger, setTrigger] = useState<
    { currency: string; address: string } | undefined
  >(undefined);

  const { data: currenciesData } = useApiRequest({
    apiToken,
    method: RequestMethod.GET,
    name: RequestName.REFERRAL_PAYOUT_CURRENCIES,
  });

  const { data, error, loading } = useApiRequest({
    apiToken,
    method: RequestMethod.POST,
    name: RequestName.REFERRAL_PAYOUT_REQUEST,
    payoutRequestParams: trigger,
  });

  const currencies: string[] = Array.isArray(currenciesData?.currencies)
    ? currenciesData.currencies
    : [];

  useEffect(() => {
    // Default the selector to the first accepted currency once loaded.
    if (!currency && currencies.length) setCurrency(currencies[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currenciesData]);

  useEffect(() => {
    if (data?.status === "success") {
      toast({
        title: `Payout requested: ${formatUsd(data.net_usd)} ${data.currency} net`,
        status: "success",
        duration: 4000,
      });
      setAddress("");
      setTrigger(undefined);
      onRequested();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    if (error) {
      // Leave the form editable and surface the server rejection (below-min,
      // pending exists, unknown currency, bad address).
      toast({
        title: serverErrorMessage(error),
        status: "error",
        duration: 5000,
      });
      setTrigger(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const preview = computePayoutPreview(available);
  const belowMinimum = available < PAYOUT_MIN_USD;
  const canSubmit =
    !belowMinimum && !!currency && address.trim().length > 0 && !loading;

  const onSubmit = () => {
    if (!canSubmit) return;
    setTrigger({ currency, address: address.trim() });
  };

  return (
    <BoxShadowContainer>
      <Stack spacing={4} width="100%">
        <Heading as="h3" size="sm">
          Request a payout
        </Heading>
        <Text color="text.secondary" fontSize={14}>
          Send your full referral balance to an external crypto wallet. A fee of
          5% (minimum {formatUsd(10)} $) applies.
        </Text>

        {belowMinimum ? (
          <Text color="text.secondary">
            You need at least {PAYOUT_MIN_USD.toFixed(2)} $ to request a payout.
            Current referral balance: {formatUsd(available)} $.
          </Text>
        ) : (
          <>
            <FormControl>
              <FormLabel>Currency</FormLabel>
              <Select
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                placeholder={currencies.length ? undefined : "Loading…"}
              >
                {currencies.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel>Wallet address</FormLabel>
              <Input
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="Enter your wallet address"
              />
            </FormControl>

            <SimpleGrid columns={3} spacing={3}>
              <Stack spacing={0}>
                <Text color="text.secondary" fontSize={13}>
                  Gross
                </Text>
                <Text fontWeight="bold">{formatUsd(preview.gross)} $</Text>
              </Stack>
              <Stack spacing={0}>
                <Text color="text.secondary" fontSize={13}>
                  Fee
                </Text>
                <Text fontWeight="bold">{formatUsd(preview.fee)} $</Text>
              </Stack>
              <Stack spacing={0}>
                <Text color="text.secondary" fontSize={13}>
                  You receive
                </Text>
                <Text fontWeight="bold">{formatUsd(preview.net)} $</Text>
              </Stack>
            </SimpleGrid>

            <Button
              colorScheme="green"
              onClick={onSubmit}
              isLoading={loading}
              isDisabled={!canSubmit}
              alignSelf="flex-start"
            >
              Request payout
            </Button>
          </>
        )}
      </Stack>
    </BoxShadowContainer>
  );
};

export default PayoutForm;
